import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  FlatList,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader, ConfirmationModal, Input } from '../components';
import { ScopedListBanner } from '../components/data-lists/ScopedListBanner';
import { ProductSelectorModal } from '../components/catalogues/ProductSelectorModal';
import { countActive, FilterField, fv, QuickView, SortOption, useListFilters } from '../components/listFilters/listFilters';
import { FilterSheet } from '../components/listFilters/FilterSheet';
import { ListFilterBar } from '../components/listFilters/ListFilterBar';
import { ANALYTICS_STATUSES, ORDER_STATUSES } from '../utils/vendorPeriods';
import { PeriodValue, VendorPeriodBar } from '../components/vendors/VendorPeriodBar';
import { apiService, VendorListQuery } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { usePermissions } from '../hooks/usePermissions';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { formatMoney, periodRange } from '../utils/vendorPeriods';
import { toCsv } from '../utils/workspaceExport';
import { saveTextFile } from '../utils/saveTextFile';

const PAGE = 20;

// Vendor fields filterable by their stored values ("More fields" in the filter sheet,
// next to custom fields) — matches VENDOR_FIXED_FIELD_PATHS in vendor.service.ts.
const VENDOR_VALUE_FIELDS = ['name', 'phone', 'email', 'crNumber', 'vatNumber'];

const day = (d?: Date) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined);

interface VendorsListScreenProps {
  /** Set when this screen is reached via a system Vendors Data List's scoped
   * mode (see DataListRecordsScreen.tsx) — restricts the list to only
   * records added THROUGH that list. */
  scopedToDataListId?: string;
  scopedListName?: string;
}

export const VendorsListScreen: React.FC<VendorsListScreenProps> = ({ scopedToDataListId, scopedListName }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const { hasPermissionAtLeast } = usePermissions();
  const canManageVendors = hasPermissionAtLeast('general.manageVendors', 'edit');
  // Sales numbers come from orders (customer data) — shown only with orders access.
  const canSeeOrders = hasPermissionAtLeast('general.manageOrders', 'view');

  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currency, setCurrency] = useState<string | undefined>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [period, setPeriod] = useState<PeriodValue>({ period: '30d' });
  // Filters — the shared list filters: remembered per screen, quick views + saved views.
  const quickViews = useMemo<QuickView[]>(() => [
    ...(canSeeOrders ? [{ key: 'active', label: t('vendors.view.hasOrders'), icon: 'receipt-outline', filters: { hasOrders: 'yes' } }] : []),
    ...(canSeeOrders ? [{ key: 'top', label: t('vendors.view.topRevenue'), icon: 'trending-up-outline', filters: { hasOrders: 'yes' }, sort: 'revenue' }] : []),
    { key: 'noProducts', label: t('vendors.view.noProducts'), icon: 'cube-outline', filters: { products: { max: 0 } } },
    { key: 'minimum', label: t('vendors.view.withMinimum'), icon: 'flag-outline', filters: { hasMinimum: 'yes' } },
  ], [t, canSeeOrders]);
  const list = useListFilters(scopedToDataListId ? `vendors:${scopedToDataListId}` : 'vendors', { quickViews });
  const [showFilters, setShowFilters] = useState(false);
  const vendorFields = useMemo<FilterField[]>(() => [
    ...(canSeeOrders ? [
      { key: 'hasOrders', label: t('vendors.filters.orders'), type: 'choice', section: t('vendors.filterSection.sales'), hint: t('vendors.filters.ordersHint'), options: [{ value: 'yes', label: t('vendors.filters.hasOrders') }, { value: 'no', label: t('vendors.filters.noOrders') }] },
      { key: 'minOrders', label: t('vendors.filters.minOrders'), type: 'range', section: t('vendors.filterSection.sales') },
      { key: 'revenue', label: t('vendors.filters.revenue'), type: 'range', section: t('vendors.filterSection.sales') },
      { key: 'statuses', label: t('vendors.filters.countedStatuses'), type: 'multi', section: t('vendors.filterSection.sales'), hint: t('vendors.filters.countedStatusesHint'), options: ORDER_STATUSES.map((st) => ({ value: st, label: t(`orders.status.${st}` as any) })) },
    ] as FilterField[] : []),
    { key: 'products', label: t('vendors.filters.products'), type: 'range', section: t('vendors.filterSection.vendor') },
    { key: 'hasMinimum', label: t('vendors.filters.minimum'), type: 'choice', section: t('vendors.filterSection.vendor'), options: [{ value: 'yes', label: t('vendors.filters.minimumSet') }, { value: 'no', label: t('vendors.filters.minimumNone') }] },
    { key: 'city', label: t('vendors.filters.city'), type: 'text', section: t('vendors.filterSection.vendor') },
    { key: 'country', label: t('vendors.filters.country'), type: 'text', section: t('vendors.filterSection.vendor') },
    { key: 'createdAt', label: t('vendors.filters.added'), type: 'date', section: t('vendors.filterSection.vendor') },
  ], [t, canSeeOrders]);
  const vendorSorts = useMemo<SortOption[]>(() => [
    { key: 'newest', label: t('vendors.sort.newest'), sortBy: 'createdAt', sortOrder: 'desc', icon: 'arrow-down-outline' },
    { key: 'name', label: t('vendors.sort.name'), sortBy: 'name', sortOrder: 'asc', icon: 'text-outline' },
    ...(canSeeOrders ? [
      { key: 'revenue', label: t('vendors.sort.revenue'), sortBy: 'revenue', sortOrder: 'desc', icon: 'cash-outline' },
      { key: 'orders', label: t('vendors.sort.orders'), sortBy: 'orders', sortOrder: 'desc', icon: 'receipt-outline' },
      { key: 'units', label: t('vendors.sort.units'), sortBy: 'units', sortOrder: 'desc', icon: 'cube-outline' },
      { key: 'lastOrder', label: t('vendors.sort.lastOrder'), sortBy: 'lastOrderAt', sortOrder: 'desc', icon: 'time-outline' },
    ] as SortOption[] : []),
    { key: 'products', label: t('vendors.sort.products'), sortBy: 'productCount', sortOrder: 'desc', icon: 'pricetags-outline' },
  ], [t, canSeeOrders]);
  const filterCount = countActive(vendorFields, list.state);

  const [customFieldKeys, setCustomFieldKeys] = useState<string[]>([]);
  const customFieldsConfig = useMemo(() => ({
    fields: [
      ...VENDOR_VALUE_FIELDS.map((key) => ({ key, label: t(`vendors.csv.${key === 'crNumber' ? 'cr' : key === 'vatNumber' ? 'vat' : key}` as any) })),
      { key: 'hasProducts', label: t('vendors.filters.hasProducts'), hasValueOnly: true },
      ...customFieldKeys.map((key) => ({ key, label: key })),
    ],
    loadValues: async (key: string) => (await apiService.getVendorCustomFieldValues(key)).data || [],
  }), [customFieldKeys, t]);

  useEffect(() => {
    apiService.getVendorCustomFieldKeys().then((res) => {
      if (res.statusCode === 1000 && res.data) setCustomFieldKeys(res.data);
    }).catch(() => {});
  }, []);

  const query: VendorListQuery = useMemo(() => {
    const f = list.state.filters;
    const sort = vendorSorts.find((x) => x.key === list.state.sort) || vendorSorts[0];
    return {
      search: fv.text(list.state.search),
      sortBy: sort.sortBy as any,
      sortOrder: sort.sortOrder,
      dataListId: scopedToDataListId,
      customFieldFilters: fv.customFields(list.state.customFields),
      withStats: true,
      ...(canSeeOrders ? periodRange(period.period, period) : {}),
      statuses: fv.list(f.statuses),
      hasOrders: f.hasOrders,
      minOrders: fv.min(f.minOrders),
      minRevenue: fv.min(f.revenue),
      maxRevenue: fv.max(f.revenue),
      minProducts: fv.min(f.products),
      maxProducts: fv.max(f.products),
      city: fv.text(f.city),
      country: fv.text(f.country),
      hasMinimum: f.hasMinimum,
      createdFrom: f.createdAt?.from,
      createdTo: f.createdAt?.to,
    };
  }, [list.state, scopedToDataListId, period, canSeeOrders, vendorSorts]);

  const loadVendors = useCallback(async (skip: number = 0) => {
    try {
      setLoading(true);
      const response = await apiService.getVendors({ ...query, limit: PAGE, skip });
      if (response.statusCode === 1000 && response.data) {
        const newVendors = response.data.vendors || [];
        setVendors((prev) => (skip ? [...prev, ...newVendors] : newVendors));
        setTotal(response.data.pagination?.total || 0);
        setCurrency(response.data.currency);
      }
    } catch (error: any) {
      console.error('Error loading vendors:', error);
      showNotification('error', t('vendors.list.error'), error.message || t('vendors.list.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [query, showNotification]);

  useEffect(() => {
    if (!list.ready) return;
    const timer = setTimeout(() => loadVendors(0), list.state.search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [loadVendors, list.ready]);

  const hasMore = vendors.length < total;
  const handleLoadMore = () => {
    if (!loading && hasMore) loadVendors(vendors.length);
  };

  // Multi-select bulk action — "Add Product" links chosen product(s) to
  // every selected vendor at once, via the same bulkLinkProducts primitive
  // Products list's "Add to Vendor" uses (just with the direction flipped:
  // many vendorIds added to few products, instead of few vendorIds added to
  // many products).
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedVendorIds, setSelectedVendorIds] = useState<string[]>([]);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev);
    setSelectedVendorIds([]);
  }, []);

  const toggleVendorSelection = useCallback((vendorId: string) => {
    setSelectedVendorIds((prev) => (prev.includes(vendorId) ? prev.filter((id) => id !== vendorId) : [...prev, vendorId]));
  }, []);

  const handleBulkAddProduct = async (productIds: string[]) => {
    setShowProductPicker(false);
    setBulkActionLoading(true);
    try {
      const res = await apiService.bulkLinkProducts({ productIds, addVendorIds: selectedVendorIds });
      if (res.statusCode === 1000) {
        showNotification('success', t('vendors.list.added'), t('vendors.list.productsAdded', { count: selectedVendorIds.length }));
        setSelectionMode(false);
        setSelectedVendorIds([]);
        loadVendors(0);
      } else {
        showNotification('error', t('vendors.list.error'), res.errors?.[0]?.message || t('vendors.list.addFailed'));
      }
    } catch (error: any) {
      showNotification('error', t('vendors.list.error'), error.message || t('vendors.list.addFailed'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!vendorToDelete) return;
    try {
      setDeleting(true);
      const response = await apiService.deleteVendor(vendorToDelete);
      if (response.statusCode === 1000) {
        showNotification('success', t('vendors.list.deleted'), t('vendors.list.deletedMessage'));
        setShowDeleteModal(false);
        setVendorToDelete(null);
        loadVendors(0);
      } else {
        showNotification('error', t('vendors.list.error'), response.errors?.[0]?.message || t('vendors.list.deleteFailed'));
      }
    } catch (error: any) {
      showNotification('error', t('vendors.list.error'), error.message || t('vendors.list.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (iso?: string) => { if (!iso) return ''; try { return new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; } };

  /** Every vendor matching the current filters as CSV (Excel opens it, Arabic included). */
  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows: any[] = [];
      for (let skip = 0; skip < 5000; skip += 200) {
        const res = await apiService.getVendors({ ...query, limit: 200, skip });
        const page = res.data?.vendors || [];
        rows.push(...page);
        if (page.length < 200) break;
      }
      const header = [t('vendors.csv.name'), t('vendors.csv.phone'), t('vendors.csv.email'), t('vendors.csv.city'), t('vendors.csv.country'), t('vendors.csv.cr'), t('vendors.csv.vat'), t('vendors.csv.products'),
        ...(canSeeOrders ? [t('vendors.csv.orders'), t('vendors.csv.units'), `${t('vendors.csv.revenue')}${currency ? ` (${currency})` : ''}`, t('vendors.csv.lastOrder')] : []),
        t('vendors.csv.minimum'), t('vendors.csv.added')];
      const grid = rows.map((v) => [v.name, v.phone, v.email, v.location?.city, v.location?.country, v.crNumber, v.vatNumber, v.stats?.productCount ?? '',
        ...(canSeeOrders ? [v.stats?.orders ?? '', v.stats?.units ?? '', v.stats?.revenue ?? '', v.stats?.lastOrderAt ? String(v.stats.lastOrderAt).slice(0, 10) : ''] : []),
        v.minimumOrderAmount ? `${v.minimumOrderCurrency || ''} ${v.minimumOrderAmount}`.trim() : '', v.createdAt ? String(v.createdAt).slice(0, 10) : '']);
      await saveTextFile(toCsv({ header, rows: grid }), `vendors-${day(new Date())}.csv`, 'text/csv');
    } catch (error: any) {
      showNotification('error', t('vendors.list.error'), error.message || t('vendors.list.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const renderVendor = ({ item }: { item: any }) => {
    const isSelected = selectedVendorIds.includes(item._id);
    const s = item.stats || {};
    const meta = [
      t('vendors.row.products', { count: s.productCount || 0 }),
      ...(canSeeOrders ? [t('vendors.row.orders', { count: s.orders || 0 }), t('vendors.row.units', { count: s.units || 0 })] : []),
    ].join(' · ');
    return (
      <TouchableOpacity
        style={[styles.vendorRow, { flexDirection: rtl.rowDirection }, isSelected && styles.vendorRowSelected]}
        onPress={() => selectionMode ? toggleVendorSelection(item._id) : navigateTo('vendor-detail', item._id, scopedToDataListId ? { listId: scopedToDataListId } : undefined)}
        activeOpacity={0.7}
      >
        {selectionMode && (
          <Ionicons name={isSelected ? 'checkmark-circle' : 'ellipse-outline'} size={22} color={isSelected ? colors.primary : colors.textLight} />
        )}
        <View style={styles.vendorAvatar}>
          <Ionicons name="business-outline" size={22} color={colors.primary} />
        </View>
        <View style={styles.vendorInfo}>
          <Text style={[styles.vendorName, { textAlign: rtl.textStart }]} numberOfLines={1}>{item.name}</Text>
          <Text style={[styles.vendorSubtext, { textAlign: rtl.textStart }]} numberOfLines={1}>
            {[item.phone, item.email, item.location?.city].filter(Boolean).join(' · ') || t('vendors.row.noContact')}
          </Text>
          <Text style={[styles.vendorMeta, { textAlign: rtl.textStart }]} numberOfLines={1}>{meta}</Text>
        </View>
        {canSeeOrders && (
          <View style={[styles.vendorNumbers, { alignItems: rtl.isRTL ? 'flex-start' : 'flex-end' }]}>
            <Text style={styles.vendorRevenue} numberOfLines={1}>{formatMoney(s.revenue || 0, currency, locale)}</Text>
            <Text style={styles.vendorLast} numberOfLines={1}>{s.lastOrderAt ? t('vendors.row.lastOrder', { date: formatDate(s.lastOrderAt) }) : t('vendors.row.noOrders')}</Text>
          </View>
        )}
        {!selectionMode && canManageVendors && (
          <TouchableOpacity
            style={styles.deleteButton}
            onPress={() => { setVendorToDelete(item._id); setShowDeleteModal(true); }}
            accessibilityLabel={t('vendors.list.delete')}
          >
            <Ionicons name="trash-outline" size={18} color={colors.error} />
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title={scopedListName || t('vendors.list.title')}
          onBack={() => navigateTo('home')}
          actions={[
            { icon: selectionMode ? 'close' : 'checkbox-outline', onPress: toggleSelectionMode, active: selectionMode, label: selectionMode ? t('vendors.list.cancelSelection') : t('vendors.list.select'), accessibilityLabel: selectionMode ? t('vendors.list.cancelSelection') : t('vendors.list.select') },
            { icon: exporting ? 'hourglass-outline' : 'download-outline', onPress: () => { if (!exporting) exportCsv(); }, label: t('vendors.list.exportCsv'), accessibilityLabel: t('vendors.list.exportCsv') },
            // Packing slips and purchase orders are designed in Document templates.
            ...(hasPermissionAtLeast('general.manageOrders', 'edit') ? [{ icon: 'documents-outline' as const, onPress: () => navigateTo('quotation-templates'), label: t('documents.templatesTitle'), accessibilityLabel: t('documents.templatesTitle') }] : []),
            ...(canManageVendors ? [
              { icon: 'cloud-upload-outline' as const, onPress: () => navigateTo('vendor-bulk-upload'), label: t('vendors.list.bulkUpload'), accessibilityLabel: t('vendors.list.bulkUpload') },
              { icon: 'add' as const, primary: true, onPress: () => navigateTo('vendor-create', undefined, scopedToDataListId ? { listId: scopedToDataListId } : undefined), accessibilityLabel: t('vendors.list.add') },
            ] : []),
          ]}
        />
      }
    >
      <View style={styles.container}>
        {scopedToDataListId && (
          <View style={styles.scopedBannerWrapper}>
            <ScopedListBanner
              moduleLabel={t('vendors.list.title')}
              moduleIcon="business-outline"
              moduleScreen="vendors"
              moduleScreenRouteParams={{}}
            />
          </View>
        )}

        <ListFilterBar
          controller={list}
          fields={vendorFields}
          sorts={vendorSorts}
          onOpenFilters={() => setShowFilters(true)}
          searchPlaceholder={t('vendors.list.search')}
          customFieldLabels={Object.fromEntries(customFieldsConfig.fields.map((f) => [f.key, f.label]))}
        />

        {canSeeOrders && (
          <View style={styles.periodWrap}>
            <VendorPeriodBar value={period} onChange={setPeriod} />
          </View>
        )}

        <Text style={[styles.totalText, { textAlign: rtl.textStart }]}>{t('vendors.list.count', { count: total })}</Text>

        {loading && vendors.length === 0 ? (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : vendors.length === 0 ? (
          <View style={styles.centerContainer}>
            <Ionicons name="business-outline" size={64} color={colors.textLight} />
            <Text style={styles.emptyText}>{filterCount || list.state.search ? t('vendors.list.noMatch') : t('vendors.list.empty')}</Text>
            {canManageVendors && !filterCount && !list.state.search && (
              <Text style={styles.emptySubtext}>{t('vendors.list.emptyHint')}</Text>
            )}
          </View>
        ) : (
          <FlatList
            data={vendors}
            renderItem={renderVendor}
            keyExtractor={(item) => item._id}
            contentContainerStyle={styles.listContent}
            onEndReached={handleLoadMore}
            onEndReachedThreshold={0.5}
            ListFooterComponent={loading && vendors.length > 0 ? (
              <View style={styles.footer}><ActivityIndicator size="small" color={colors.primary} /></View>
            ) : null}
          />
        )}
      </View>

      <ConfirmationModal
        visible={showDeleteModal}
        title={t('vendors.list.deleteTitle')}
        message={t('vendors.list.deleteMessage')}
        confirmText={t('vendors.list.delete')}
        type="error"
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => { setShowDeleteModal(false); setVendorToDelete(null); }}
      />

      <FilterSheet
        visible={showFilters}
        title={t('vendors.filters.title')}
        fields={vendorFields}
        sorts={vendorSorts}
        value={list.state}
        customFields={customFieldsConfig}
        onApply={list.apply}
        onClose={() => setShowFilters(false)}
      />

      {selectionMode && selectedVendorIds.length > 0 && (
        <View style={[styles.bulkActionBar, { flexDirection: rtl.rowDirection }]}>
          <Text style={styles.bulkActionCount}>{t('vendors.list.selected', { count: selectedVendorIds.length })}</Text>
          <TouchableOpacity style={styles.bulkActionButton} onPress={() => setShowProductPicker(true)} disabled={bulkActionLoading}>
            <Ionicons name="cube-outline" size={16} color={colors.white} />
            <Text style={styles.bulkActionButtonText}>{t('vendors.list.addProduct')}</Text>
          </TouchableOpacity>
          {bulkActionLoading && <ActivityIndicator size="small" color={colors.white} style={styles.bulkActionLoading} />}
        </View>
      )}

      <ProductSelectorModal
        visible={showProductPicker}
        onClose={() => setShowProductPicker(false)}
        onConfirm={handleBulkAddProduct}
        title={t('vendors.list.addProductTitle')}
        confirmText={t('vendors.list.addConfirm')}
        allowMultiple
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.base },
  scopedBannerWrapper: { paddingHorizontal: spacing.lg, paddingTop: spacing.sm },
  searchContainer: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  searchInput: { flex: 1, marginBottom: 0 },
  periodWrap: { paddingHorizontal: spacing.md, paddingTop: spacing.sm },
  activeChips: { gap: spacing.xs, paddingHorizontal: spacing.md, paddingTop: spacing.sm, alignItems: 'center' },
  activeChip: {
    alignItems: 'center',
    gap: 4,
    maxWidth: 240,
    paddingVertical: 4,
    paddingHorizontal: spacing.sm,
    borderRadius: 999,
    backgroundColor: (colors.primary as any) + '15',
  },
  activeChipText: { fontSize: typography.fontSize.xs, color: colors.primary, fontWeight: '600' },
  clearAll: { paddingHorizontal: spacing.sm },
  clearAllText: { fontSize: typography.fontSize.xs, color: colors.textLight, fontWeight: '600' },
  totalText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
  },
  listContent: { padding: spacing.md },
  vendorRow: {
    alignItems: 'center',
    padding: spacing.md,
    marginBottom: spacing.sm,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    gap: spacing.sm,
  },
  vendorRowSelected: {
    borderColor: colors.primary,
  },
  bulkActionBar: {
    position: 'absolute',
    left: spacing.lg,
    right: spacing.lg,
    bottom: spacing.lg,
    backgroundColor: colors.textDark,
    borderRadius: designTokens.borderRadius.lg,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bulkActionCount: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
  },
  bulkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primary,
  },
  bulkActionButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.xs,
    fontWeight: '600',
  },
  bulkActionLoading: {
    marginStart: spacing.sm,
  },
  vendorAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: (colors.primary as any) + '15',
    alignItems: 'center',
    justifyContent: 'center',
  },
  vendorInfo: { flex: 1 },
  vendorName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  vendorSubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    marginTop: 2,
  },
  vendorMeta: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    marginTop: 2,
  },
  vendorNumbers: { maxWidth: 140 },
  vendorRevenue: { fontSize: typography.fontSize.sm, fontWeight: '700', color: colors.textDark },
  vendorLast: { fontSize: 11, color: colors.textLight, marginTop: 2 },
  deleteButton: { padding: spacing.xs },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  emptyText: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    marginTop: spacing.md,
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    marginTop: spacing.xs,
  },
  footer: { padding: spacing.md, alignItems: 'center' },
});
